import React, { useState, useEffect } from 'react';
import { RefreshCw, Settings, ChevronUp, ChevronDown, Wifi, WifiOff, X } from 'lucide-react';
import { formatNumber, formatPercentage } from '../utils/formatters';
import { formatCryptoIDR } from '../utils/currency';
import toast from 'react-hot-toast';
import { useCoinData } from '../contexts/CoinDataContext';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { useSignalR } from '../hooks/useSignalR';
import { csrfFetch } from '../utils/csrfFetch';
import { generateIdempotencyKey } from '../utils/idempotency';

const EXCHANGE = {
  Indodax: 0,
  Binance: 1,
  GateIo: 2,
  Mexc: 3,
  Bybit: 4
};

const exchangeIdToName = Object.fromEntries(
  Object.entries(EXCHANGE).map(([name, id]) => [id, name])
);

const getExchangeId = (name) => {
  if (!name) return null;
  const normalized = typeof name === 'string'
    ? name.replace(/\./g, '').replace(/\s+/g, '').trim()
    : name;
  if (EXCHANGE[normalized] || EXCHANGE[normalized] === 0) return EXCHANGE[normalized];

  const matchedKey = Object.keys(EXCHANGE).find(
    (key) => key.toLowerCase() === String(normalized).toLowerCase()
  );

  return matchedKey ? EXCHANGE[matchedKey] : null;
};

const getHiddenKey = (symbol, buyExchangeId, sellExchangeId) => {
  if (!symbol || !Number.isInteger(buyExchangeId) || !Number.isInteger(sellExchangeId)) return null;
  return `${symbol.toUpperCase()}-${buyExchangeId}-${sellExchangeId}`;
};

const ArbitrageOpportunities = () => {
  const { coinData } = useCoinData();
  const { appSettings, submitAppSettings } = useAppSettings();
  const { opportunities, isConnected, connectionState, refreshData } = useSignalR();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showHiddenDialog, setShowHiddenDialog] = useState(false);
  
  // Quick settings editing state - only when actively editing
  const [editingQuickSettings, setEditingQuickSettings] = useState(null);
  
  // Hidden coins state
  const [hiddenCoins, setHiddenCoins] = useState([]);
  const [lastSavedHiddenCoins, setLastSavedHiddenCoins] = useState([]);
  const [isHiddenLoading, setIsHiddenLoading] = useState(false);
  const [isSavingHidden, setIsSavingHidden] = useState(false);
  const [coinMap, setCoinMap] = useState({});
  
  // Current quick settings to display (either appSettings or being edited)
  const currentQuickSettings = editingQuickSettings || {
    percentageDifference: (appSettings?.defaultPercentageDifference ?? 1).toString(),
    minimalVolumeIDR: (appSettings?.defaultMinimalVolume ?? 1000000).toString(),
    quickBuyVolumeIDR: (appSettings?.quickBuyDefaultVolume ?? 9000000).toString()
  };
  
  // Check if settings have changed
  const hasSettingsChanged = editingQuickSettings !== null;

  // Volume step from app settings
  const volumeStep = appSettings?.volumeSteps || 500000;
  
  // Jackpot threshold from app settings
  const jackpotThreshold = appSettings?.jackpotPercentage || 5;

  const formatWithDots = (value, options = {}) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value ?? '-';
    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 5,
      ...options,
    }).format(num);
  };

  // Format number with dots for thousands
  const formatNumberInput = (value) => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '');
    // Add dots for thousands
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Parse formatted number back to plain number
  const parseFormattedNumber = (value) => {
    return value.replace(/\./g, '');
  };

  // Handle volume step up/down
  const handleVolumeStep = (field, direction) => {
    // Start editing mode if not already editing
    if (!editingQuickSettings) {
      setEditingQuickSettings({ ...currentQuickSettings });
    }
    
    const currentValue = parseInt(currentQuickSettings[field]) || 0;
    const newValue = direction === 'up' 
      ? currentValue + volumeStep 
      : Math.max(0, currentValue - volumeStep);
    
    setEditingQuickSettings(prev => ({
      ...prev,
      [field]: newValue.toString()
    }));
  };

  const handleBuyClick = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowConfirmDialog(true);
  };

  const handleBuy = async (opportunity) => {
    setShowConfirmDialog(false);
    toast.loading(`Executing arbitrage for ${opportunity.coin}...`, { id: 'arb-trade' });
    
    try {
      // Call backend API to execute trade on Indodax
      const response = await csrfFetch('/exchange/indodax/trade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': generateIdempotencyKey(),
        },
        credentials: 'include',
        body: JSON.stringify({
          pair: opportunity.pair || `${opportunity.coin.toLowerCase()}_idr`,
          tradeType: "buy",
          orderType: "market", 
          price: opportunity.askPrice,
          idr: currentQuickSettings.quickBuyVolumeIDR,
          amount: parseFloat(currentQuickSettings.quickBuyVolumeIDR) / opportunity.askPrice
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const boughtAmount = parseFloat(currentQuickSettings.quickBuyVolumeIDR) / opportunity.askPrice;
        toast.success(
          <div>
            <p>Arbitrage executed successfully!</p>
            <p className="text-sm mt-1">
              Bought {boughtAmount.toFixed(8)} {opportunity.coin} on {opportunity.fromExchange}
            </p>
            <p className="text-sm">
              Spent: Rp {parseFloat(currentQuickSettings.quickBuyVolumeIDR).toLocaleString('id-ID')}
            </p>
          </div>,
          { id: 'arb-trade', duration: 5000 }
        );
      } else {
        const errorData = await response.json();
        toast.error(`Trade failed: ${errorData.message || 'Unknown error'}`, { id: 'arb-trade' });
      }
    } catch (error) {
      toast.error(`Trade failed: ${error.message}`, { id: 'arb-trade' });
    }
  };

  const fetchCoins = async () => {
    const response = await csrfFetch('/coins', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch coins: ${response.status} ${errorText}`);
    }

    return response.json();
  };

  const loadHiddenCoins = async () => {
    setIsHiddenLoading(true);
    try {
      const response = await csrfFetch('/coinFilter', {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to load hidden coins: ${response.status}`);
      }
      const coins = await response.json();
      const list = (Array.isArray(coins) ? coins : [])
        .map((coin) => {
          const id = coin?.id ?? coin?.coinId ?? coin?.coin?.id;
          const symbol = (coin?.symbol || coin?.coinSymbol || coin?.coin?.symbol || coin?.name || '').toUpperCase();
          const buyExchangeId = Number.isInteger(coin?.buyExchange)
            ? coin.buyExchange
            : getExchangeId(coin?.buyExchangeName || coin?.exchangeFrom);
          const sellExchangeId = Number.isInteger(coin?.sellExchange)
            ? coin.sellExchange
            : getExchangeId(coin?.sellExchangeName || coin?.exchangeTo);

          if (!id || !symbol || !Number.isInteger(buyExchangeId) || !Number.isInteger(sellExchangeId)) {
            return null;
          }

          return {
            id,
            symbol,
            name: coin?.name || coin?.coinName || coin?.coin?.name || symbol,
            buyExchangeId,
            sellExchangeId,
            buyExchangeName: exchangeIdToName[buyExchangeId] || coin?.buyExchangeName || coin?.exchangeFrom,
            sellExchangeName: exchangeIdToName[sellExchangeId] || coin?.sellExchangeName || coin?.exchangeTo,
            fromExchange: coin?.fromExchange || coin?.exchangeFrom || coin?.buyExchangeName,
            toExchange: coin?.toExchange || coin?.exchangeTo || coin?.sellExchangeName
          };
        })
        .filter(Boolean);
      setHiddenCoins(list);
      setLastSavedHiddenCoins(list);
    } catch (error) {
      toast.error('Failed to load hidden coins. Showing all for now.');
    } finally {
      setIsHiddenLoading(false);
    }
  };

  const loadCoinMap = async () => {
    try {
      const coins = await fetchCoins();
      const map = {};
      if (Array.isArray(coins)) {
        coins.forEach(c => {
          if (c?.symbol && Number.isInteger(c?.id)) {
            map[c.symbol.toUpperCase()] = c.id;
          }
        });
      }
      setCoinMap(map);
    } catch (error) {
  
      toast.error('Failed to load coin list for hiding.');
    }
  };

  const handleCoinsChange = async ({ action, opportunity, coinId, coinSymbol, buyExchangeId, sellExchangeId }) => {
    const symbol = (opportunity?.coin || coinSymbol || '').toUpperCase(); // normalize symbol
    const resolvedBuyExchangeId = Number.isInteger(buyExchangeId)
      ? buyExchangeId
      : getExchangeId(opportunity?.fromExchange);
    const resolvedSellExchangeId = Number.isInteger(sellExchangeId)
      ? sellExchangeId
      : getExchangeId(opportunity?.toExchange);

    if (!Number.isInteger(resolvedBuyExchangeId) || !Number.isInteger(resolvedSellExchangeId)) {
      toast.error('Cannot update hidden coins: missing exchange information');
      return;
    }

    const resolvedId = Number(coinMap[symbol] ?? opportunity?.coinId ?? opportunity?.assetId ?? opportunity?.id ?? coinId);
    if (!Number.isInteger(resolvedId)) {
      toast.error('Cannot hide coin: missing coin id');
      return;
    }

    let nextHiddenCoins = [...hiddenCoins]; // start from current list

    if (action === 'hide') {
      const existing = hiddenCoins.find(
        (c) => c.symbol?.toUpperCase() === symbol && c.buyExchangeId === resolvedBuyExchangeId && c.sellExchangeId === resolvedSellExchangeId
      ); // already hidden?
      if (existing) {
        toast.success(`${symbol} is already hidden for this route`);
        return;
      }

      nextHiddenCoins = [
        ...hiddenCoins,
        {
          id: resolvedId,
          name: opportunity?.coin || coinSymbol,
          symbol: symbol,
          buyExchangeId: resolvedBuyExchangeId,
          sellExchangeId: resolvedSellExchangeId,
          buyExchangeName: exchangeIdToName[resolvedBuyExchangeId] || opportunity?.fromExchange,
          sellExchangeName: exchangeIdToName[resolvedSellExchangeId] || opportunity?.toExchange,
          fromExchange: opportunity?.fromExchange,
          toExchange: opportunity?.toExchange
        }
      ]; // add to list
    } else if (action === 'unhide') {
      nextHiddenCoins = hiddenCoins.filter(
        (c) => !(c.id === resolvedId && c.buyExchangeId === resolvedBuyExchangeId && c.sellExchangeId === resolvedSellExchangeId)
      ); // remove from list
    } else {
      return; // unknown action
    }

    setIsSavingHidden(true); // block UI while saving
    try {
      if (action === 'hide') {
        await csrfFetch('/coinFilter', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            coinId: resolvedId,
            buyExchange: resolvedBuyExchangeId,
            sellExchange: resolvedSellExchangeId
          }),
        }); // persist coin+exchange pair to /coinFilter
        setHiddenCoins(nextHiddenCoins); // update UI state
        setLastSavedHiddenCoins(nextHiddenCoins); // remember for rollback
        toast.success(`${symbol || 'Coin'} hidden for this route`);
      } else if (action === 'unhide') {
        await csrfFetch('/coinFilter', {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            coinId: resolvedId,
            buyExchange: resolvedBuyExchangeId,
            sellExchange: resolvedSellExchangeId
          }),
        }); // delete specific coin+exchange on server
        setHiddenCoins(nextHiddenCoins); // update UI state
        setLastSavedHiddenCoins(nextHiddenCoins); // remember for rollback
        toast.success(`${symbol || 'Coin'} unhidden for this route`);
      }
    } catch (error) {

      setHiddenCoins(lastSavedHiddenCoins); // rollback
      toast.error(error.message || 'Failed to update hidden coins');
    } finally {
      setIsSavingHidden(false); // release UI
    }
  };

  const handleRefresh = async () => {
    if (!isConnected) {
      toast.error('Not connected to server');
      return;
    }
    
    setIsRefreshing(true);
    
    try {
      await refreshData();
      toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to refresh data');

    } finally {
      setIsRefreshing(false);
    }
  };

  const handleQuickSettingChange = (field, value) => {
    // Start editing mode if not already editing
    if (!editingQuickSettings) {
      setEditingQuickSettings({ ...currentQuickSettings });
    }
    
    if (field === 'percentageDifference') {
      // For percentage, allow decimals but remove non-numeric except dots
      const cleanValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
      setEditingQuickSettings(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    } else {
      // For IDR fields, format with dots
      const plainNumber = parseFormattedNumber(value);
      const formattedValue = formatNumberInput(value);
      setEditingQuickSettings(prev => ({
        ...prev,
        [field]: plainNumber
      }));
    }
  };

  const saveQuickSettings = async () => {
    try {
      // Create updated settings object with all current settings plus the quick changes
      const updatedSettings = {
        ...appSettings,
        defaultPercentageDifference: parseFloat(editingQuickSettings.percentageDifference) || 1,
        defaultMinimalVolume: parseInt(editingQuickSettings.minimalVolumeIDR) || 1000000,
        quickBuyDefaultVolume: parseInt(editingQuickSettings.quickBuyVolumeIDR) || 9000000
      };
      
      // Save using AppSettings API
      await submitAppSettings(updatedSettings);
      setEditingQuickSettings(null);
      toast.success('Quick settings saved successfully');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to save quick settings');
    }
  };
  
  const cancelQuickSettings = () => {
    setEditingQuickSettings(null);
  };

  useEffect(() => {
    loadHiddenCoins();
    loadCoinMap();
  }, []);

  const hiddenKeySet = new Set(
    hiddenCoins
      .map(c => getHiddenKey(c.symbol, c.buyExchangeId, c.sellExchangeId))
      .filter(Boolean)
  );

  const sortedHiddenCoins = [...hiddenCoins].sort((a, b) => {
    const nameA = (a?.symbol || a?.name || '').toUpperCase();
    const nameB = (b?.symbol || b?.name || '').toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  const isOpportunityHidden = (opportunity) => {
    const buyExchangeId = getExchangeId(opportunity?.fromExchange);
    const sellExchangeId = getExchangeId(opportunity?.toExchange);
    const key = getHiddenKey(opportunity?.coin, buyExchangeId, sellExchangeId);
    if (!key) return false;
    return hiddenKeySet.has(key);
  };
  const minimalVolumeIDR = parseInt(currentQuickSettings.minimalVolumeIDR) || 0;

  // Sort opportunities by highest profit first without mutating original array
  const sortedOpportunities = [...opportunities].sort(
    (a, b) => (b?.profit ?? -Infinity) - (a?.profit ?? -Infinity)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-white">Arbitrage Opportunities</h1>
              <div className="flex items-center space-x-2 text-sm">
                {isConnected ? (
                  <>
                    <Wifi className="h-4 w-4 text-green-500" />
                    <span className="text-green-500">Connected</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-4 w-4 text-red-500" />
                    <span className="text-red-500">{connectionState}</span>
                  </>
                )}
              </div>
            </div>
            <p className="text-gray-400">Real-time cross-exchange price differences</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm text-gray-400">USDT/IDR</p>
            <p className="text-xl font-bold text-white">
              {coinData?.bid ? formatCryptoIDR(coinData.bid) : 'Loading...'}
            </p>
          </div>
          <button
            onClick={() => setShowHiddenDialog(true)}
            className="btn-secondary flex items-center disabled:opacity-50"
            disabled={isHiddenLoading}
          >
            Hidden Coins
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || !isConnected}
            className="btn-secondary flex items-center disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Quick Settings */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Quick Settings
          </h3>
          {hasSettingsChanged && (
            <div className="flex space-x-2">
              <button
                onClick={cancelQuickSettings}
                className="px-4 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveQuickSettings}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
              >
                Save Settings
              </button>
            </div>
          )}
        </div>
          
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Percentage Difference (%)
            </label>
            <input
              type="number"
              value={currentQuickSettings.percentageDifference}
              onChange={(e) => handleQuickSettingChange('percentageDifference', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="1"
              step="0.1"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Minimal Volume (IDR)
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={formatNumberInput(currentQuickSettings.minimalVolumeIDR)}
                onChange={(e) => handleQuickSettingChange('minimalVolumeIDR', e.target.value)}
                className="w-full px-3 py-2 pr-16 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1.000.000"
              />
              <div className="absolute right-0 flex flex-col h-full">
                <button
                  onClick={() => handleVolumeStep('minimalVolumeIDR', 'up')}
                  className="px-3 flex-1 text-gray-400 hover:text-white hover:bg-gray-700 transition-all rounded-tr-md border-l border-gray-700"
                  type="button"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleVolumeStep('minimalVolumeIDR', 'down')}
                  className="px-3 flex-1 text-gray-400 hover:text-white hover:bg-gray-700 transition-all rounded-br-md border-l border-t border-gray-700"
                  type="button"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Quick Buy Volume (IDR)
            </label>
            <input
              type="text"
              value={formatNumberInput(currentQuickSettings.quickBuyVolumeIDR)}
              onChange={(e) => handleQuickSettingChange('quickBuyVolumeIDR', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="5.000.000"
            />
          </div>
        </div>
      </div>

      {/* Opportunities Tables Side by Side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Left Table: INDODAX → International */}
        <div className="glass-card">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">INDODAX → International</h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Coin</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Route</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Indodax Price</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Indodax Volume</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Profit %</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {sortedOpportunities.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-400">
                      {isConnected ? 'No arbitrage opportunities found' : 'Connecting to server...'}
                    </td>
                  </tr>
                ) : (
                  sortedOpportunities
                    .filter(opp => opp.fromExchange === 'Indodax')
                    .filter(opp => !isOpportunityHidden(opp))
                    .filter(opp => opp.profit >= parseFloat(currentQuickSettings.percentageDifference))
                    .filter(opp => (opp.askVolume * opp.askPrice) >= minimalVolumeIDR)
                    .map((opp, index) => {
                      const isJackpot = opp.profit >= jackpotThreshold;
                      return (
                        <tr key={`${opp.coin}-${opp.toExchange}-${index}`} className={`transition-all ${
                          isJackpot 
                            ? 'bg-gradient-to-r from-amber-900/50 to-yellow-900/40 hover:from-amber-900/60 hover:to-yellow-900/50 border-l-4 border-amber-400 shadow-lg' 
                            : 'hover:bg-gray-800/50'
                        }`}>
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{opp.coin}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-300 text-sm">{opp.fromExchange} → {opp.toExchange}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-white font-mono text-sm">Rp {formatWithDots(opp.askPrice)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-gray-300 font-mono text-sm">Rp {formatWithDots((opp.askVolumeIdr).toFixed(0))}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium text-sm ${
                            opp.profit >= 2 ? 'text-green-500' : 
                            opp.profit >= 1 ? 'text-yellow-500' : 
                            'text-gray-500'
                          }`}>
                            {opp.profit.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleCoinsChange({ action: 'hide', opportunity: opp })}
                              disabled={isSavingHidden}
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md transition-colors disabled:opacity-60"
                            >
                              Hide
                            </button>
                            <button
                              onClick={() => handleBuyClick(opp)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-md transition-colors"
                            >
                              Buy
                            </button>
                          </div>
                        </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Table: International → INDODAX */}
        <div className="glass-card">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">International → INDODAX</h2>
              <span className="text-sm text-gray-400">Min profit: 1%</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Coin</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Route</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Indodax Price</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Indodax Volume</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Profit %</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {sortedOpportunities.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-400">
                      {isConnected ? 'No arbitrage opportunities found' : 'Connecting to server...'}
                    </td>
                  </tr>
                ) : (
                  sortedOpportunities
                    .filter(opp => opp.toExchange === 'Indodax')
                    .filter(opp => !isOpportunityHidden(opp))
                    .filter(opp => opp.profit >= parseFloat(currentQuickSettings.percentageDifference))
                    .filter(opp => (opp.bidVolume * opp.bidPrice) >= minimalVolumeIDR)
                    .map((opp, index) => {
                      const isJackpot = opp.profit >= jackpotThreshold;
                      return (
                        <tr key={`${opp.coin}-${opp.fromExchange}-${index}`} className={`transition-all ${
                          isJackpot 
                            ? 'bg-gradient-to-r from-yellow-900/40 to-yellow-800/30 hover:from-yellow-900/50 hover:to-yellow-800/40 border-l-4 border-yellow-400 shadow-lg' 
                            : 'hover:bg-gray-800/50'
                        }`}>
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{opp.coin}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-gray-300 text-sm">{opp.fromExchange} → {opp.toExchange}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-white font-mono text-sm">Rp {formatWithDots(opp.bidPrice)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-gray-300 font-mono text-sm">Rp {formatWithDots((opp.bidVolumeIdr).toFixed(0))}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium text-sm ${
                            opp.profit >= 2 ? 'text-green-500' : 
                            opp.profit >= 1 ? 'text-yellow-500' : 
                            'text-gray-500'
                          }`}>
                            {opp.profit.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => handleCoinsChange({ action: 'hide', opportunity: opp })}
                              disabled={isSavingHidden}
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-md transition-colors disabled:opacity-60"
                            >
                              Hide
                            </button>
                            {/* <button
                              onClick={() => {}}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-md transition-colors"
                            >
                              Development
                            </button> */}
                          </div>
                        </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Hidden Coins Dialog */}
      {showHiddenDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-lg w-full p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Hidden Coins</h3>
              <button
                onClick={() => setShowHiddenDialog(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {isHiddenLoading ? (
              <p className="text-gray-400">Loading hidden coins...</p>
            ) : hiddenCoins.length === 0 ? (
              <p className="text-gray-400">No hidden coins yet.</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {sortedHiddenCoins.map((coin) => (
                  <div
                    key={`${coin.id}-${coin.symbol}-${coin.buyExchangeId}-${coin.sellExchangeId}`}
                    className="flex items-center justify-between bg-gray-800/50 px-3 py-2 rounded-md"
                  >
                    <div>
                      <p className="text-white font-medium">{coin.symbol || coin.name}</p>
                      <p className="text-xs text-gray-500">
                        {(coin.fromExchange || coin.buyExchangeName || exchangeIdToName[coin.buyExchangeId] || 'Unknown')} → {(coin.toExchange || coin.sellExchangeName || exchangeIdToName[coin.sellExchangeId] || 'Unknown')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCoinsChange({
                        action: 'unhide',
                        coinId: coin.id,
                        coinSymbol: coin.symbol || coin.name,
                        buyExchangeId: coin.buyExchangeId,
                        sellExchangeId: coin.sellExchangeId
                      })}
                      disabled={isSavingHidden}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors disabled:opacity-60"
                    >
                      Unhide
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="glass-card max-w-md w-full p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">
              Confirm Purchase
            </h3>

            <h3 className="text-lg font-semibold text-white mb-4">
             Are you sure you want to buy {selectedOpportunity.coin} with this detail?
            </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Price:</span>
                <span className="text-white">{formatCryptoIDR(selectedOpportunity.askPrice)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Volume (IDR):</span>
                <span className="text-white">{formatNumber(parseInt(currentQuickSettings.quickBuyVolumeIDR))}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Amount:</span>
                <span className="text-white">{(parseFloat(currentQuickSettings.quickBuyVolumeIDR) / selectedOpportunity.askPrice).toFixed(8)} {selectedOpportunity.coin}</span>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleBuy(selectedOpportunity)}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArbitrageOpportunities;
